import { RefObject, useEffect, useRef, useState } from "react";
import { useRafThrottle } from "src/domains/shared/hooks/useRafThrottle";
import {
  lastPointerPositionIn,
  trackLastPointerPosition,
} from "src/domains/shared/utils/lastPointerPosition";

trackLastPointerPosition();

export type CursorPosition = { x: number; y: number };

/**
 * pointer 이벤트 기반 — 지도 라이브러리가 pointerdown 을 preventDefault 하면 드래그 중 호환 mousemove 가
 * 억제되므로 mousemove 로 바꾸면 안 된다. 리스너는 마운트 시점의 containerRef.current 에 부착된다.
 */
export function useContainerCursorPosition(
  containerRef: RefObject<HTMLElement | null>,
  onPositionFrame: (position: CursorPosition) => void
) {
  const [inside, setInside] = useState(false);
  // 이동 빈도 경로에서 React 상태를 건드리지 않는다 — 진입·이탈 전이에만 setState 한다
  const insideRef = useRef(false);
  const notifyPosition = useRafThrottle<CursorPosition>(onPositionFrame);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    function onMove(event: PointerEvent) {
      if (!insideRef.current) {
        insideRef.current = true;
        setInside(true);
      }
      notifyPosition({ x: event.clientX, y: event.clientY });
    }

    function onLeave() {
      insideRef.current = false;
      setInside(false);
    }

    // 켜지는 순간 포인터가 이미 컨테이너 위면(배치 버튼을 누르고 그대로 둔 경우) 움직이기 전에도 바로 보인다
    const initial = lastPointerPositionIn(container);
    if (initial) {
      insideRef.current = true;
      setInside(true);
      notifyPosition({ x: initial.clientX, y: initial.clientY });
    }

    container.addEventListener("pointermove", onMove, { passive: true });
    container.addEventListener("pointerleave", onLeave);
    return () => {
      container.removeEventListener("pointermove", onMove);
      container.removeEventListener("pointerleave", onLeave);
      insideRef.current = false;
    };
  }, [containerRef]);

  return { inside };
}
